/**
 * Employee Authentication Service
 * Handles authentication for agency employees
 */

import apiClient, { handleApiResponse, handleApiError } from './api';
import type { ApiResponse } from '../types';

export interface EmployeeLoginCredentials {
  email: string;
  password: string;
  agencySlug: string;
}

export interface EmployeeAuthResponse {
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  expiresIn: string;
  employee: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    fullName: string;
    role: string;
    department: string;
    permissions: any;
    avatar?: string;
    mustChangePassword: boolean;
  };
  agency: {
    id: string;
    businessName: string;
    displayName: string;
    slug: string;
    status: string;
    timezone?: {
      name?: string;
      offsetMinutes?: number;
    };
  };
}

export interface AgencySearchResult {
  _id: string;
  businessName: string;
  displayName: string;
  slug: string;
  address?: {
    city?: string;
    governorate?: string;
  };
}

class EmployeeAuthService {
  private refreshUnavailableForToken: string | null = null;
  /**
   * The agency this server is dedicated to (DEDICATED_AGENCY_SLUG on the backend), or
   * null on the central server - where employees search for their agency instead.
   */
  async getDedicatedAgency(): Promise<AgencySearchResult | null> {
    try {
      const response = await apiClient.get<ApiResponse<{ dedicatedAgency: AgencySearchResult | null }>>(
        '/agency/public/installation'
      );
      return handleApiResponse(response).data?.dedicatedAgency || null;
    } catch {
      // An older backend has no such endpoint: behave like the central server.
      return null;
    }
  }

  /**
   * Search for agencies by name
   * @param query Search term
   * @returns List of matching agencies
   */
  async searchAgencies(query: string): Promise<AgencySearchResult[]> {
    try {
      if (!query || query.trim().length === 0) {
        return [];
      }

      const response = await apiClient.get<ApiResponse<{ agencies: AgencySearchResult[] }>>(
        `/agency/public/search?q=${encodeURIComponent(query)}`
      );
      
      const data = handleApiResponse(response);
      return data.data?.agencies || [];
    } catch (error) {
      console.error('Failed to search agencies:', error);
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Login as employee
   * @param credentials Email, password, and agency slug
   * @returns Auth response with tokens and employee data
   */
  async login(credentials: EmployeeLoginCredentials): Promise<ApiResponse<EmployeeAuthResponse>> {
    try {
      const response = await apiClient.post<ApiResponse<EmployeeAuthResponse>>(
        '/employee-auth/login',
        credentials
      );

      const data = handleApiResponse(response);

      // Store tokens in localStorage with employee prefix
      if (data.success && data.data?.accessToken) {
        localStorage.setItem('employee_token', data.data.accessToken);
        localStorage.setItem('employee_refresh_token', data.data.refreshToken);
        localStorage.setItem('employee_user', JSON.stringify({
          ...data.data.employee,
          agency: data.data.agency
        }));
        localStorage.setItem('user_type', 'employee');
      }

      return data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Logout employee
   */
  logout(): void {
    localStorage.removeItem('employee_token');
    localStorage.removeItem('employee_refresh_token');
    localStorage.removeItem('employee_user');
    localStorage.removeItem('user_type');
  }

  /**
   * Get current employee from localStorage
   */
  getCurrentEmployee(): EmployeeAuthResponse['employee'] & { agency: EmployeeAuthResponse['agency'] } | null {
    const userStr = localStorage.getItem('employee_user');
    if (!userStr) return null;
    
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  }

  /**
   * Re-fetch the agency timezone for a stored employee session.
   * Sessions created before the login response included it have no timezone,
   * which blocks booking creation/confirmation.
   */
  async refreshAgencyTimezone(): Promise<ReturnType<EmployeeAuthService['getCurrentEmployee']>> {
    const employee = this.getCurrentEmployee();
    if (!employee) return null;
    const token = this.getToken();
    if (token && token === this.refreshUnavailableForToken) return null;

    try {
      const response = await apiClient.get<ApiResponse<{ employee: EmployeeAuthResponse['employee']; agency: EmployeeAuthResponse['agency'] }>>(
        '/employee-auth/me',
        { skipAuthRedirect: true, backgroundRequest: true } as any
      );
      const data = handleApiResponse(response);
      const timezone = data.data?.agency?.timezone;
      if (!data.success || !data.data?.employee || this.getCurrentEmployee()?.id !== employee.id) return this.getCurrentEmployee();
      const updated = { ...employee, ...data.data.employee, agency: { ...employee.agency, ...(timezone && { timezone }) } };
      localStorage.setItem('employee_user', JSON.stringify(updated));
      return updated;
    } catch (error) {
      console.error('Failed to refresh employee agency timezone:', error);
      const status = (error as { response?: { status?: number } }).response?.status;
      if (token && (status === 403 || status === 404)) this.refreshUnavailableForToken = token;
      return null;
    }
  }

  /**
   * Get employee token
   */
  getToken(): string | null {
    return localStorage.getItem('employee_token');
  }

  /**
   * Check if user is employee
   */
  isEmployee(): boolean {
    return localStorage.getItem('user_type') === 'employee';
  }

  /**
   * Get employee agency slug
   */
  getAgencySlug(): string | null {
    const employee = this.getCurrentEmployee();
    return employee?.agency?.slug || null;
  }

  /**
   * Change employee's own password
   * @param currentPassword Current temporary password
   * @param newPassword New secure password
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<ApiResponse> {
    try {
      const response = await apiClient.put<ApiResponse>(
        '/agency/employees/me/password',
        { currentPassword, newPassword }
      );
      
      const result = handleApiResponse(response);
      
      if (result.success) {
        const employee = this.getCurrentEmployee();
        if (employee) {
          employee.mustChangePassword = false;
          localStorage.setItem('employee_user', JSON.stringify(employee));
        }
      }
      
      return result;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }
}

export default new EmployeeAuthService();
